<script>
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import BlockingContainer from '@components/BlockingContainer';
import Layout from '@layouts/main';
import AllowanceModel from '@models/Allowance';
import HeaderComponent from '@components/HeaderComponent';

export default {
  page() {
    return {
      title: this.$t('titles.allowances'),
    };
  },
  components: {
    Layout,
    FontAwesomeIcon,
    BlockingContainer,
    HeaderComponent,
  },
  data() {
    return {
      sortBy: 'country',
      sortDesc: false,
      totalRows: null,
      perPage: 20,
      currentPage: 1,
      fieldsTable: [
        { key: 'id', label: this.$t('table_labels.id'), sortable: true },
        { key: 'country', label: this.$t('table_labels.country'), sortable: true },
        { key: 'meals', label: this.$t('table_labels.meals'), sortable: true },
        { key: 'meals_24', label: this.$t('table_labels.meals_24'), sortable: true },
        { key: 'overnight_stay', label: this.$t('table_labels.overnight_stay'), sortable: true },
        { key: 'actions', label: this.$t('table_labels.actions'), sortable: false },
      ],
      isBusy: false,
    };
  },
  computed: {
    sortDir() {
      return this.sortDesc ? '-' : '';
    },
  },
  methods: {
    restore(id) {
      if (confirm(this.$t('alerts.restore_entity', { entity: this.$t('entities.allowance') }))) {
        this.$wait.start('allowance processing');
        new AllowanceModel({
          id: `${id}/restore`,
        })
          .save()
          .then(() => {
            this.$refs.asyncTable.refresh();
            this.$wait.end('allowance processing');
          })
          .catch(() => {
            this.$wait.end('allowance processing');
          });
      }
    },
    itemsProvider(ctx) {
      return AllowanceModel.orderBy(this.sortDir + ctx.sortBy)
        .page(this.currentPage)
        .limit(ctx.perPage)
        .get()
        .then((response) => {
          const items = response.data;
          this.totalRows = response.meta.total;
          return items || [];
        })
        .catch(() => []);
    },
    async deleteEntity(id) {
      if (confirm(this.$t('alerts.delete_entity', { entity: this.$t('entities.allowance') }))) {
        this.$wait.start('allowance processing');
        const allowance = new AllowanceModel({ id });
        await allowance.delete();
        this.$wait.end('allowance processing');
        this.$refs.asyncTable.refresh();
      }
    },
  },
};
</script>

<template>
  <Layout>
    <b-row class="mb-4">
      <b-col>
        <HeaderComponent :title="$t('titles.allowances')" :button-text="$t('titles.create_allowance')" @button-pressed="$router.push({ name: 'allowances.create' })"></HeaderComponent>
      </b-col>
    </b-row>
    <div id="allowances-index" class="position-relative">
      <BlockingContainer v-if="$wait.is('allowance processing') || isBusy"></BlockingContainer>
      <b-table
        ref="asyncTable"
        small
        striped
        :items="itemsProvider"
        stacked="md"
        :busy.sync="isBusy"
        :current-page="currentPage"
        :per-page="perPage"
        :sort-by.sync="sortBy"
        :sort-desc.sync="sortDesc"
        :fields="fieldsTable"
        :no-sort-reset="true"
        :show-empty="true"
      >
        <!-- custom sorting icons -->
        <template #head()="{ label, field: { key, sortable } }">
          {{ label }}
          <template v-if="sortable">
            <!-- Custom icons -->
            <template>
              <FontAwesomeIcon v-if="sortBy !== key" :icon="['fas', 'arrows-alt-v']" />
              <FontAwesomeIcon v-else-if="sortDesc" :icon="['fas', 'arrow-down']" />
              <FontAwesomeIcon v-else :icon="['fas', 'arrow-up']" />
            </template>
          </template>
        </template>
        <template v-slot:cell(country)="row">
          <span>{{ row.item.country.name_de }}</span>
        </template>
        <template v-slot:cell(actions)="row">
          <div class="d-flex">
            <b-btn class="mr-2" size="sm" variant="outline-primary" :to="{ name: 'allowances.edit', params: { id: row.item.id } }">
              <FontAwesomeIcon :icon="['fas', 'edit']" />
            </b-btn>
            <b-btn v-if="row.item.deleted_at === null" size="sm" variant="outline-danger" @click.stop="deleteEntity(row.item.id)">
              <FontAwesomeIcon :icon="['fas', 'times-circle']" />
            </b-btn>
            <b-btn v-else v-can:superAdministrate size="sm" variant="outline-success" @click.stop="restore(row.item.id)">
              <FontAwesomeIcon :icon="['fas', 'trash-restore']" />
            </b-btn>
          </div>
        </template>
        <template v-slot:cell(empty)="row">
          <div class="text-center">{{ $t('messages.no_items') }}</div>
        </template>
      </b-table>
    </div>

    <b-row>
      <b-col sm="12">
        <b-pagination v-model="currentPage" :total-rows="totalRows" :per-page="perPage" />
      </b-col>
    </b-row>
  </Layout>
</template>
